
import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Toaster, toast } from 'sonner';
import { ArrowUpRight, Mail, Clock, ChevronDown, MessageCircle } from 'lucide-react';

const WEB3FORMS_KEY = '8cd0e0ed-15df-4f32-9f62-598a231718f8';
const WHATSAPP_NUMBER = '19706378362'; // international, digits only

const serviceKeys = ['services.s1', 'services.s2', 'services.s3', 'services.s4'];

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', service: '' });
  const [selectOpen, setSelectOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceSelect = (key) => {
    setFormData((prev) => ({ ...prev, service: key }));
    setSelectOpen(false);
  };

  // Build a prefilled message for the WhatsApp fallback
  const buildBody = () => [
    `Hi Horizon team,`,
    ``,
    formData.name ? `My name: ${formData.name}` : null,
    formData.email ? `Email: ${formData.email}` : null,
    formData.service ? `Service requested: ${t(formData.service)}` : null,
    ``,
    `I'd like to talk about a project — please get back to me when you can.`,
    ``,
    `Thanks!`,
  ].filter(Boolean).join('\n');

  // Real form submit — Web3Forms delivers the email to your inbox automatically.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.service) {
      toast.error(t('contact.select_service_error'));
      return;
    }
    setSending(true);
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `Project inquiry — ${t(formData.service)}`,
          from_name: 'Horizon Digital Agency — Website',
          name: formData.name,
          email: formData.email,
          service: t(formData.service),
          message: buildBody(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(t('contact.success'));
        setFormData({ name: '', email: '', service: '' });
      } else {
        toast.error(data.message || t('contact.error'));
        // eslint-disable-next-line no-console
        console.error('Web3Forms rejected:', data);
      }
    } catch (err) {
      toast.error(t('contact.error'));
      // eslint-disable-next-line no-console
      console.error('Form submit failed:', err);
    } finally {
      setSending(false);
    }
  };

  const openWhatsApp = () => {
    if (!formData.service) {
      toast.error(t('contact.select_service_error'));
      return;
    }
    const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildBody())}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-14 lg:py-20 bg-[#F5EDE4] relative overflow-hidden">
      <Toaster position="top-center" richColors />

      {/* Animated background typography */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
        <motion.div className="absolute whitespace-nowrap" style={{ top: '10%' }}
          animate={{ x: ['0%', '-40%'] }} transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}>
          <span className="text-[14vw] font-bold text-[#1A1816]/[0.025] tracking-[-0.05em] font-sans leading-none">
            CONTACT&nbsp;&nbsp;&nbsp;US&nbsp;&nbsp;&nbsp;GET&nbsp;&nbsp;&nbsp;IN&nbsp;&nbsp;&nbsp;TOUCH&nbsp;&nbsp;&nbsp;CONTACT
          </span>
        </motion.div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left — Info */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-[1px] bg-[#C9A96E]" />
              <span className="text-[9px] font-semibold tracking-[0.3em] uppercase text-[#8A857D]">{t('contact.label')}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-bold text-[#1A1816] leading-tight mb-4">
              {t('contact.title1')}<br /><span className="font-serif italic text-[#C9A96E]">{t('contact.title2')}</span>
            </h2>
            <p className="text-sm sm:text-base text-[#8A857D] leading-relaxed mb-10 max-w-md">{t('contact.sub')}</p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 border border-[#C9A96E]/30 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#C9A96E]" />
                </div>
                <div>
                  <p className="text-xs text-[#8A857D] mb-0.5">{t('contact.email')}</p>
                  <a href="mailto:contact@horizondigitalagency.org" className="text-sm text-[#1A1816] font-medium hover:text-[#C9A96E] transition-colors">
                    contact@horizondigitalagency.org
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 border border-[#C9A96E]/30 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-[#C9A96E]" />
                </div>
                <div>
                  <p className="text-xs text-[#8A857D] mb-0.5">{t('contact.response_label')}</p>
                  <p className="text-sm text-[#1A1816] font-medium">{t('contact.response')}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right — Form */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <div className="bg-[#1A1816] p-8 sm:p-10">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-[10px] tracking-[0.2em] uppercase text-[#FAF8F5]/40 mb-3">{t('contact.name')}</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required
                    data-testid="contact-name-input"
                    className="w-full bg-transparent border-b border-[#FAF8F5]/15 focus:border-[#C9A96E] text-[#FAF8F5] py-3 text-sm outline-none transition-colors placeholder:text-[#FAF8F5]/20"
                    placeholder={t('contact.name_ph')} />
                </div>
                <div>
                  <label htmlFor="email" className="block text-[10px] tracking-[0.2em] uppercase text-[#FAF8F5]/40 mb-3">{t('contact.email')}</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                    data-testid="contact-email-input"
                    className="w-full bg-transparent border-b border-[#FAF8F5]/15 focus:border-[#C9A96E] text-[#FAF8F5] py-3 text-sm outline-none transition-colors placeholder:text-[#FAF8F5]/20"
                    placeholder={t('contact.email_ph')} />
                </div>

                {/* Service Selector */}
                <div className="relative">
                  <label className="block text-[10px] tracking-[0.2em] uppercase text-[#FAF8F5]/40 mb-3">{t('contact.service_label')}</label>
                  <button
                    type="button"
                    data-testid="contact-service-select"
                    onClick={() => setSelectOpen(!selectOpen)}
                    className="w-full bg-transparent border-b border-[#FAF8F5]/15 focus:border-[#C9A96E] text-left py-3 text-sm outline-none transition-colors flex items-center justify-between"
                  >
                    <span className={formData.service ? 'text-[#FAF8F5]' : 'text-[#FAF8F5]/20'}>
                      {formData.service ? t(formData.service) : t('contact.service_ph')}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-[#FAF8F5]/40 transition-transform duration-300 ${selectOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {selectOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#2D2926] border border-[#FAF8F5]/10"
                    >
                      {serviceKeys.map((key) => (
                        <button
                          key={key}
                          type="button"
                          data-testid={`service-option-${key}`}
                          onClick={() => handleServiceSelect(key)}
                          className={`block w-full text-left px-4 py-3 text-sm transition-colors hover:bg-[#C9A96E]/10 ${
                            formData.service === key ? 'text-[#C9A96E] font-semibold' : 'text-[#FAF8F5]/70'
                          }`}
                        >
                          {t(key)}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  <button type="submit" disabled={sending} data-testid="contact-submit-button"
                    className="bg-[#C9A96E] text-[#1A1816] py-4 font-semibold text-xs tracking-wider uppercase hover:bg-[#E8D5B5] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                    {sending ? (
                      <span>{t('common.sending')}</span>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        <span>{t('common.send_message')}</span>
                      </>
                    )}
                  </button>
                  <button type="button" onClick={openWhatsApp} data-testid="contact-whatsapp-button"
                    className="bg-[#25D366] text-white py-4 font-semibold text-xs tracking-wider uppercase hover:bg-[#1FB855] transition-colors flex items-center justify-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    <span>{t('common.whatsapp')}</span>
                  </button>
                </div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#FAF8F5]/40 text-center mt-1">
                  {t('common.no_payment_reply')}
                </p>
              </form>
            </div>
          </motion.div>
        </div>

        {/* Final CTA */}
        <motion.div className="mt-16 pt-12 border-t border-[#1A1816]/10 text-center"
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h3 className="text-xl sm:text-2xl font-sans font-bold text-[#1A1816] mb-3">{t('contact.final_title')}</h3>
          <p className="text-sm text-[#8A857D] mb-6 max-w-md mx-auto">{t('contact.final_sub')}</p>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            data-testid="final-cta-top"
            className="inline-flex items-center gap-2 border border-[#1A1816] text-[#1A1816] px-8 py-3.5 text-[10px] font-semibold tracking-wider uppercase hover:bg-[#1A1816] hover:text-[#FAF8F5] transition-colors">
            <span>{t('contact.final_cta')}</span>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
